import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import clsx from "clsx";

const API = "https://api.vyapakprint.com/api";

const STATUS_OPTIONS = [
  "Pending",
  "Printing",
  "Packing",
  "Dispatched",
  "Cancelled",
];

const ADDON_LABELS = {
  dieShape: "Die Shape",
  goldFoil: "Gold Foil",
  goldColor: "Gold Color",
  spotUV: "Spot UV",
  mattelColor: "Mattel Color",
  binding: "Binding",
  size: "Size Customization",
  paperType: "Paper Type",
  flapOpening: "Flap Opening",
  windowCutting: "Window Cutting",
  printing: "Printing",
  laminationType: "Lamination Type",
  textureType: "Texture Type",
};

const BusinessOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [remarkInput, setRemarkInput] = useState("");
  const [statusImage, setStatusImage] = useState(null);
  const [statusImagePreview, setStatusImagePreview] = useState("");
  const [statusFormError, setStatusFormError] = useState("");

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!statusImage) {
      setStatusImagePreview("");
      return;
    }

    const objectUrl = URL.createObjectURL(statusImage);
    setStatusImagePreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [statusImage]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setPageError(null);

      const { data } = await axios.get(
        `${API}/businessOrders/getBusinessOrderById/${id}`,
      );

      setOrder(data?.order || null);
    } catch (error) {
      console.error(error);
      setPageError("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  const userDetails = order?.userDetails || {};
  const walletHistory = order?.walletHistory || {};
  const customizations = order?.customizations || {};
  const calculationSummary = order?.calculationSummary || {};
  const statusHistory = Array.isArray(order?.statusHistory)
    ? order.statusHistory
    : [];

  const populatedUser =
    order?.userId && typeof order.userId === "object" ? order.userId : null;
  const populatedProduct =
    order?.productId && typeof order.productId === "object"
      ? order.productId
      : null;
  const populatedCategory =
    order?.category && typeof order.category === "object"
      ? order.category
      : null;

  const userIdValue =
    typeof order?.userId === "object" ? order?.userId?._id : order?.userId;

  const productIdValue =
    typeof order?.productId === "object"
      ? order?.productId?._id
      : order?.productId;

  const categoryIdValue =
    typeof order?.category === "object"
      ? order?.category?._id
      : order?.category;

  const productNameValue = order?.productName || populatedProduct?.name || "—";
  const categoryNameValue =
    order?.categoryName || populatedCategory?.name || "—";

  const requiresRemark =
    selectedStatus === "Cancelled" || selectedStatus === "Dispatched";
  const requiresImage = false;

  const canShowActions = useMemo(() => {
    if (!order?.status) return true;
    return !["Delivered", "Cancelled"].includes(order.status);
  }, [order?.status]);

  const resetStatusModal = () => {
    setStatusModalOpen(false);
    setSelectedStatus("");
    setRemarkInput("");
    setStatusImage(null);
    setStatusImagePreview("");
    setStatusFormError("");
  };

  const openStatusModal = (status) => {
    if (order?.status === status) return;

    setSelectedStatus(status);
    setRemarkInput(
      status === "Cancelled"
        ? "Cancelled due to..."
        : status === "Dispatched"
          ? "Dispatched via..."
          : "",
    );
    setStatusImage(null);
    setStatusImagePreview("");
    setStatusFormError("");
    setStatusModalOpen(true);
  };

  const handleStatusImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setStatusImage(null);
      return;
    }
    setStatusImage(file);
  };

  const validateStatusForm = () => {
    if (requiresRemark && !remarkInput.trim()) {
      setStatusFormError("Remark is required for this status.");
      return false;
    }

    if (requiresImage && !statusImage) {
      setStatusFormError("Dispatch image is required.");
      return false;
    }

    setStatusFormError("");
    return true;
  };

  const submitStatusUpdate = async () => {
    if (!selectedStatus || !validateStatusForm()) return;

    try {
      setIsUpdatingStatus(true);

      if (selectedStatus === "Cancelled") {
        await axios.patch(`${API}/businessOrders/${id}/cancel`, {
          remark: remarkInput.trim(),
        });
      } else if (selectedStatus === "Dispatched") {
        const formData = new FormData();
        formData.append("status", selectedStatus);
        formData.append("remark", remarkInput.trim());
        if (statusImage) {
          formData.append("image", statusImage);
        }

        await axios.put(
          `${API}/businessOrders/updateBusinessOrderStatus/${id}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
      } else {
        await axios.put(
          `${API}/businessOrders/updateBusinessOrderStatus/${id}`,
          {
            status: selectedStatus,
            remark: remarkInput.trim(),
          },
        );
      }

      await fetchOrder();
      resetStatusModal();
      navigate(-1);
    } catch (error) {
      console.error(error);
      setStatusFormError(
        error?.response?.data?.message ||
          "Failed to update status. Please try again.",
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  const getFileExtension = (url) => {
    try {
      const cleanUrl = String(url || "")
        .split("?")[0]
        .split("#")[0];
      const lastPart = cleanUrl.split("/").pop() || "";
      const ext = lastPart.includes(".") ? lastPart.split(".").pop() : "";
      return ext || "file";
    } catch {
      return "file";
    }
  };

  const sanitizeFileName = (value) => {
    return String(value || "")
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const handleDownloadFile = async (fileUrl) => {
    if (!fileUrl) return;

    try {
      const ext = getFileExtension(fileUrl);
      const orderNo = sanitizeFileName(order?._id || "order");
      const productTitle = sanitizeFileName(productNameValue || "product");
      const fileName = `${orderNo}-${productTitle}.${ext}`;

      const response = await fetch(fileUrl, { mode: "cors" });
      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `₹${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return "—";

    return parsed.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Pending: "bg-amber-50 text-amber-700 border-amber-200",
      Processing: "bg-sky-50 text-sky-700 border-sky-200",
      Printing: "bg-yellow-50 text-yellow-700 border-yellow-200",
      Packing: "bg-indigo-50 text-indigo-700 border-indigo-200",
      Shipped: "bg-purple-50 text-purple-700 border-purple-200",
      Dispatched: "bg-emerald-50 text-emerald-700 border-emerald-200",
      Delivered: "bg-green-50 text-green-700 border-green-200",
      Cancelled: "bg-rose-50 text-rose-700 border-rose-200",
    };

    return (
      <span
        className={clsx(
          "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border",
          statusConfig[status] || "bg-slate-50 text-slate-700 border-slate-200",
        )}
      >
        <span className="h-2 w-2 rounded-full bg-current opacity-70" />
        {status || "Unknown"}
      </span>
    );
  };

  const hasCustomizations = () => {
    if (!customizations || typeof customizations !== "object") return false;

    return Object.entries(customizations).some(([_, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim() !== "";
      if (typeof value === "number") return !Number.isNaN(value);
      if (typeof value === "boolean") return value === true;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object") return Object.keys(value).length > 0;
      return false;
    });
  };

  const hasCalculationSummary = () => {
    if (!calculationSummary || typeof calculationSummary !== "object") {
      return false;
    }

    return (
      Number(calculationSummary.basePricePerUnit || 0) > 0 ||
      Number(calculationSummary.addonPricePerUnit || 0) > 0 ||
      Number(calculationSummary.finalPricePerUnit || 0) > 0 ||
      Number(calculationSummary.totalPrice || 0) > 0 ||
      (Array.isArray(calculationSummary.addons) &&
        calculationSummary.addons.length > 0)
    );
  };

  const formatCustomizationKey = (key) => {
    if (ADDON_LABELS[key]) return ADDON_LABELS[key];

    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const displayValue = (value) => {
    if (value === null || value === undefined) return "Not specified";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const shouldDisplayValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "number") return !Number.isNaN(value);
    if (typeof value === "boolean") return value === true;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return false;
  };

  const safeText = (value, fallback = "—") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "object") return fallback;
    const text = String(value).trim();
    return text || fallback;
  };

  const calculationAddons = Array.isArray(calculationSummary?.addons)
    ? calculationSummary.addons
    : [];

  const normalizedCustomizationEntries = Object.entries(customizations || {})
    .filter(([_, value]) => shouldDisplayValue(value))
    .sort(([a], [b]) => {
      const aLabel = formatCustomizationKey(a);
      const bLabel = formatCustomizationKey(b);
      return aLabel.localeCompare(bLabel);
    });

  const cardClass =
    "bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden";
  const sectionHeaderClass =
    "px-5 md:px-6 py-4 border-b border-slate-100 flex items-center justify-between";
  const sectionTitleClass = "text-lg font-semibold text-slate-800";
  const infoLabelClass =
    "text-xs font-semibold uppercase tracking-wide text-slate-500";
  const infoValueClass =
    "mt-1 text-sm md:text-base text-slate-800 font-medium break-words";

  const InfoBlock = ({ label, value, mono = false, action = null }) => (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className={infoLabelClass}>{label}</p>
      <div className="mt-1 flex items-start justify-between gap-3">
        <p
          className={clsx(
            "text-sm md:text-base text-slate-800 font-medium break-all",
            mono && "font-mono text-xs md:text-sm",
          )}
        >
          {safeText(value)}
        </p>
        {action}
      </div>
    </div>
  );

  const SummaryCard = ({ label, value, valueClassName = "" }) => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <p className="text-sm text-slate-500">{label}</p>
      <p
        className={clsx(
          "mt-2 text-2xl font-bold text-slate-900 break-words",
          valueClassName,
        )}
      >
        {safeText(value)}
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="h-14 w-14 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-rose-200 rounded-2xl p-8 text-center shadow-sm">
          <div className="h-14 w-14 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center">
            <svg
              className="w-7 h-7 text-rose-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-rose-700 font-semibold">{pageError}</p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-2.5 text-slate-700 font-medium hover:bg-slate-50"
            >
              Go Back
            </button>
            <button
              onClick={fetchOrder}
              className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-white font-medium hover:bg-slate-800"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center shadow-sm border border-slate-200">
          <p className="text-slate-700 font-semibold text-lg">No order found</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-5 inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-white font-medium hover:bg-slate-800"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="flex items-start gap-3">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 shrink-0"
                type="button"
                title="Go Back"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                  Business Order Details
                </h1>

                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                  <span className="rounded-lg bg-white border border-slate-200 px-3 py-1 font-mono text-xs md:text-sm">
                    #{order._id}
                  </span>

                  <span className="rounded-lg bg-white border border-slate-200 px-3 py-1">
                    {productNameValue}
                  </span>

                  <span className="rounded-lg bg-white border border-slate-200 px-3 py-1">
                    {categoryNameValue}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm min-w-[260px]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">
                    Current Status
                  </p>
                  <div className="mt-2">{getStatusBadge(order.status)}</div>
                </div>

                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">
                    Order Date
                  </p>
                  <p className="mt-2 text-sm font-medium text-slate-800">
                    {formatDateTime(order.orderDate)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
            <SummaryCard label="Quantity" value={order.quantity || 0} />
            <SummaryCard
              label="Base Price / Unit"
              value={formatCurrency(order.basePricePerUnit || 0)}
              valueClassName="text-sky-600"
            />
            <SummaryCard
              label="Add-ons / Unit"
              value={formatCurrency(order.addonPricePerUnit || 0)}
              valueClassName="text-indigo-600"
            />
            <SummaryCard
              label="Final Price / Unit"
              value={formatCurrency(order.unitPrice || 0)}
              valueClassName="text-violet-600"
            />
            <SummaryCard
              label="Total Price"
              value={formatCurrency(order.totalPrice)}
              valueClassName="text-emerald-600"
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <div className="xl:col-span-8 space-y-6">
              <section className={cardClass}>
                <div className={sectionHeaderClass}>
                  <h2 className={sectionTitleClass}>Customer Information</h2>
                </div>

                <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoBlock
                    label="Business Name"
                    value={
                      userDetails.businessName || populatedUser?.businessName
                    }
                  />
                  <InfoBlock
                    label="Contact Person"
                    value={
                      userDetails.contactPerson || populatedUser?.contactPerson
                    }
                  />
                  <InfoBlock
                    label="Mobile"
                    value={userDetails.mobile || populatedUser?.mobile}
                  />
                  <InfoBlock
                    label="Email"
                    value={userDetails.email || populatedUser?.email}
                  />
                  <InfoBlock label="City" value={userDetails.city} />
                  <InfoBlock label="Pin Code" value={userDetails.pinCode} />

                  <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className={infoLabelClass}>Shipping Address</p>
                    <p className={infoValueClass}>
                      {safeText(userDetails.shippingAddress)}
                    </p>
                  </div>
                </div>
              </section>

              {(productNameValue !== "—" ||
                categoryNameValue !== "—" ||
                populatedProduct?.description ||
                populatedProduct?.minimumQuantity ||
                populatedProduct?.shippingTime ||
                populatedProduct?.image) && (
                <section className={cardClass}>
                  <div className={sectionHeaderClass}>
                    <h2 className={sectionTitleClass}>Product Information</h2>
                  </div>

                  <div className="p-5 md:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoBlock
                        label="Product Name"
                        value={productNameValue}
                      />
                      <InfoBlock label="Category" value={categoryNameValue} />
                      <InfoBlock
                        label="Minimum Quantity"
                        value={populatedProduct?.minimumQuantity}
                      />
                      <InfoBlock
                        label="Shipping Time"
                        value={populatedProduct?.shippingTime}
                      />
                      <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className={infoLabelClass}>Description</p>
                        <p className={infoValueClass}>
                          {safeText(populatedProduct?.description)}
                        </p>
                      </div>
                    </div>

                    {populatedProduct?.image && (
                      <div className="mt-4">
                        <p className={`${infoLabelClass} mb-2`}>
                          Product Image
                        </p>
                        <img
                          src={populatedProduct.image}
                          alt={productNameValue}
                          className="w-full max-w-sm rounded-2xl border border-slate-200 object-cover"
                        />
                      </div>
                    )}
                  </div>
                </section>
              )}

              {hasCalculationSummary() && (
                <section className={cardClass}>
                  <div className={sectionHeaderClass}>
                    <h2 className={sectionTitleClass}>Pricing Breakdown</h2>
                  </div>

                  <div className="p-5 md:p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoBlock
                        label="Base Price / Unit"
                        value={formatCurrency(
                          calculationSummary.basePricePerUnit ||
                            order.basePricePerUnit,
                        )}
                      />
                      <InfoBlock
                        label="Add-ons Total / Unit"
                        value={formatCurrency(
                          calculationSummary.addonPricePerUnit ||
                            order.addonPricePerUnit,
                        )}
                      />
                      <InfoBlock
                        label="Final Price / Unit"
                        value={formatCurrency(
                          calculationSummary.finalPricePerUnit ||
                            order.unitPrice,
                        )}
                      />
                      <InfoBlock
                        label="Quantity"
                        value={
                          calculationSummary.quantity || order.quantity || "0"
                        }
                      />
                    </div>

                    {calculationAddons.length > 0 && (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className={infoLabelClass}>Selected Add-ons</p>

                        <div className="mt-3 space-y-3">
                          {calculationAddons.map((item, index) => (
                            <div
                              key={`${item?.key || item?.label || "addon"}-${index}`}
                              className="rounded-xl border border-slate-200 bg-white p-4"
                            >
                              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                                <div>
                                  <p className="text-sm font-semibold text-slate-800">
                                    {safeText(
                                      item?.label ||
                                        ADDON_LABELS[item?.key] ||
                                        "Add-on",
                                    )}
                                    {safeText(item?.value, "") !== "—" &&
                                    safeText(item?.value, "") !== ""
                                      ? ` (${safeText(item?.value, "")})`
                                      : ""}
                                  </p>

                                  {item?.selected ? (
                                    <p className="text-xs text-slate-500 mt-1">
                                      Selected: {String(item.selected)}
                                    </p>
                                  ) : null}
                                </div>

                                <div className="text-left md:text-right">
                                  <p className="text-sm font-medium text-slate-800">
                                    + {formatCurrency(item?.pricePerUnit)} /
                                    unit
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    Total: {formatCurrency(item?.totalPrice)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                        Final Total
                      </p>
                      <p className="mt-2 text-2xl font-bold text-emerald-700">
                        {formatCurrency(
                          calculationSummary.totalPrice || order.totalPrice,
                        )}
                      </p>
                    </div>
                  </div>
                </section>
              )}

              {hasCustomizations() && (
                <section className={cardClass}>
                  <div className={sectionHeaderClass}>
                    <h2 className={sectionTitleClass}>
                      Product Customizations
                    </h2>
                  </div>

                  <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {normalizedCustomizationEntries.map(([key, value]) => (
                      <div
                        key={key}
                        className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <p className={infoLabelClass}>
                          {formatCustomizationKey(key)}
                        </p>
                        <p className="mt-1 text-sm font-medium text-slate-800 break-words">
                          {displayValue(value)}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {order.file && (
                <section className={cardClass}>
                  <div className={sectionHeaderClass}>
                    <h2 className={sectionTitleClass}>Product File</h2>
                  </div>

                  <div className="p-5 md:p-6">
                    <div className="rounded-2xl border border-sky-200 bg-sky-50/70 p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          Customer uploaded design file
                        </p>
                        <p className="text-sm text-slate-500 mt-1 break-all">
                          Review the file before printing / dispatching.
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <a
                          href={order.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                        >
                          Open
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDownloadFile(order.file)}
                          className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700"
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              <section className={cardClass}>
                <div className={sectionHeaderClass}>
                  <h2 className={sectionTitleClass}>Wallet History</h2>
                </div>

                <div className="p-5 md:p-6">
                  {walletHistory?.remark ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoBlock
                        label="Amount"
                        value={formatCurrency(walletHistory.amount)}
                      />

                      <InfoBlock
                        label="Receipt ID"
                        value={walletHistory.receiptId}
                        mono
                        action={
                          walletHistory.receiptId ? (
                            <button
                              onClick={() => copyText(walletHistory.receiptId)}
                              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-slate-100 shrink-0"
                              type="button"
                            >
                              Copy
                            </button>
                          ) : null
                        }
                      />

                      <InfoBlock
                        label="Previous Balance"
                        value={formatCurrency(walletHistory.previousBalance)}
                      />

                      <InfoBlock
                        label="New Balance"
                        value={formatCurrency(walletHistory.newBalance)}
                      />

                      <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className={infoLabelClass}>Remark</p>
                        <p className={infoValueClass}>
                          {safeText(walletHistory.remark)}
                        </p>
                      </div>

                      <InfoBlock
                        label="Transaction Date"
                        value={formatDateTime(walletHistory.date)}
                      />
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
                      No wallet history available
                    </div>
                  )}
                </div>
              </section>

              <section className={cardClass}>
                <div className={sectionHeaderClass}>
                  <h2 className={sectionTitleClass}>Status History</h2>
                </div>

                <div className="p-5 md:p-6">
                  {statusHistory.length > 0 ? (
                    <div className="space-y-4">
                      {statusHistory.map((item, index) => (
                        <div
                          key={`${item?.status || "status"}-${
                            item?.changedAt || index
                          }`}
                          className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                        >
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              {getStatusBadge(item?.status)}
                              <p className="mt-3 text-sm text-slate-700">
                                {safeText(item?.remark, "No remark")}
                              </p>
                              <p className="mt-2 text-xs text-slate-500">
                                {formatDateTime(item?.changedAt)}
                              </p>
                            </div>

                            {item?.image ? (
                              <a
                                href={item.image}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                              >
                                View Image
                              </a>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
                      No status history available
                    </div>
                  )}
                </div>
              </section>
            </div>

            <div className="xl:col-span-4">
              <div className="space-y-6 xl:sticky xl:top-6">
                <section className={cardClass}>
                  <div className={sectionHeaderClass}>
                    <h2 className={sectionTitleClass}>Status Management</h2>
                  </div>

                  <div className="p-5 md:p-6">
                    <div className="mb-4">
                      <p className="text-sm text-slate-500 mb-2">
                        Current Status
                      </p>
                      {getStatusBadge(order.status)}
                    </div>

                    {order.file && (
                      <div className="mb-5 rounded-xl border border-sky-200 bg-sky-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-sky-700 mb-2">
                          Product File
                        </p>

                        <div className="flex flex-wrap gap-2">
                          <a
                            href={order.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-100 transition"
                          >
                            Open
                          </a>

                          <button
                            type="button"
                            onClick={() => handleDownloadFile(order.file)}
                            className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 transition"
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    )}

                    {canShowActions ? (
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-slate-700">
                          Update Status
                        </p>

                        {STATUS_OPTIONS.map((status) => {
                          const isDisabled =
                            order.status === status || isUpdatingStatus;

                          return (
                            <button
                              key={status}
                              type="button"
                              disabled={isDisabled}
                              onClick={() => openStatusModal(status)}
                              className={clsx(
                                "w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all border text-left",
                                isDisabled
                                  ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                                  : status === "Cancelled"
                                    ? "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                                    : status === "Dispatched"
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                      : status === "Printing"
                                        ? "bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100"
                                        : status === "Packing"
                                          ? "bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100"
                                          : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100",
                              )}
                            >
                              Mark as {status}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                        This order is already <strong>{order.status}</strong>.
                        No further action is available.
                      </div>
                    )}
                  </div>
                </section>

                <section className={cardClass}>
                  <div className={sectionHeaderClass}>
                    <h2 className={sectionTitleClass}>Order Summary</h2>
                  </div>

                  <div className="p-5 md:p-6 space-y-4">
                    <InfoBlock
                      label="Order ID"
                      value={order._id}
                      mono
                      action={
                        order?._id ? (
                          <button
                            onClick={() => copyText(order._id)}
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-slate-100 shrink-0"
                            type="button"
                          >
                            Copy
                          </button>
                        ) : null
                      }
                    />

                    <InfoBlock
                      label="User ID"
                      value={userIdValue}
                      mono
                      action={
                        userIdValue ? (
                          <button
                            onClick={() => copyText(userIdValue)}
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-slate-100 shrink-0"
                            type="button"
                          >
                            Copy
                          </button>
                        ) : null
                      }
                    />

                    <InfoBlock
                      label="Product ID"
                      value={productIdValue}
                      mono
                      action={
                        productIdValue ? (
                          <button
                            onClick={() => copyText(productIdValue)}
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-slate-100 shrink-0"
                            type="button"
                          >
                            Copy
                          </button>
                        ) : null
                      }
                    />

                    <InfoBlock
                      label="Category ID"
                      value={categoryIdValue}
                      mono
                      action={
                        categoryIdValue ? (
                          <button
                            onClick={() => copyText(categoryIdValue)}
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-slate-100 shrink-0"
                            type="button"
                          >
                            Copy
                          </button>
                        ) : null
                      }
                    />

                    <InfoBlock label="Product Name" value={productNameValue} />
                    <InfoBlock label="Category" value={categoryNameValue} />
                    <InfoBlock label="Job Title" value={order.firmName} />
                    <InfoBlock label="Quantity" value={order.quantity || 0} />
                    <InfoBlock
                      label="Base Price / Unit"
                      value={formatCurrency(order.basePricePerUnit || 0)}
                    />
                    <InfoBlock
                      label="Add-ons / Unit"
                      value={formatCurrency(order.addonPricePerUnit || 0)}
                    />
                    <InfoBlock
                      label="Final Price / Unit"
                      value={formatCurrency(order.unitPrice || 0)}
                    />

                    <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                        Total Price
                      </p>
                      <p className="mt-2 text-2xl font-bold text-emerald-700">
                        {formatCurrency(order.totalPrice)}
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>

      {statusModalOpen && (
        <div className="fixed inset-0 z-[1000] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100">
              <h3 className="text-xl font-semibold text-slate-900">
                Update Order Status
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                You are changing the order status to{" "}
                <span className="font-semibold text-slate-800">
                  {selectedStatus}
                </span>
                .
              </p>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Selected Status
                </label>
                <div>{getStatusBadge(selectedStatus)}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Remark{" "}
                  {requiresRemark ? (
                    <span className="text-rose-500">*</span>
                  ) : null}
                </label>
                <textarea
                  value={remarkInput}
                  onChange={(e) => setRemarkInput(e.target.value)}
                  rows={4}
                  placeholder={
                    selectedStatus === "Cancelled"
                      ? "Enter cancellation reason"
                      : selectedStatus === "Dispatched"
                        ? "Enter courier / dispatch details"
                        : "Optional remark"
                  }
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 resize-none"
                />
              </div>

              {selectedStatus === "Dispatched" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Dispatch Image
                  </label>

                  <label className="flex items-center justify-center w-full rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 cursor-pointer hover:bg-slate-100 transition">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleStatusImageChange}
                    />
                    <span className="text-sm text-slate-600 font-medium">
                      {statusImage
                        ? statusImage.name
                        : "Click to upload image (optional)"}
                    </span>
                  </label>

                  {statusImagePreview && (
                    <div className="mt-4">
                      <img
                        src={statusImagePreview}
                        alt="Dispatch preview"
                        className="w-full h-56 object-cover rounded-xl border border-slate-200"
                      />
                    </div>
                  )}
                </div>
              )}

              {statusFormError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {statusFormError}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={resetStatusModal}
                disabled={isUpdatingStatus}
                className="rounded-xl border border-slate-300 px-4 py-2.5 text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={submitStatusUpdate}
                disabled={isUpdatingStatus}
                className="rounded-xl bg-slate-900 px-4 py-2.5 text-white font-medium hover:bg-slate-800 disabled:opacity-50"
              >
                {isUpdatingStatus ? "Updating..." : "Confirm Update"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BusinessOrderDetails;
